import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../components/MenuBar";
import "@ui5/webcomponents/dist/Table.js";
import "@ui5/webcomponents/dist/TableRow.js";
import "@ui5/webcomponents/dist/TableCell.js";
import "@ui5/webcomponents/dist/TableHeaderRow.js";
import "@ui5/webcomponents/dist/TableHeaderCell.js";
import "@ui5/webcomponents/dist/Toast.js";
import "@ui5/webcomponents/dist/Bar.js";
import "@ui5/webcomponents/dist/Label.js";
import "@ui5/webcomponents/dist/Input.js";
import "@ui5/webcomponents/dist/Button.js";
import "@ui5/webcomponents/dist/ComboBox.js";
import "@ui5/webcomponents/dist/Select";
import "@ui5/webcomponents/dist/Option";

const ViewRecords = () => {
  const [alerts, setAlerts] = useState([]);
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    alertId: "",
    accountNumber: "",
    status: "",
    date: "",
    time: "", // Add time filter
  });

  const statusMapping = {
    H: "Handled",
    U: "Unhandled",
    C: "Completed",
    I: "Invalid Call",
  };

  const reverseStatusMapping = {
    Handled: "H",
    Unhandled: "U",
    Completed: "C",
    "Invalid Call": "I",
  };

  const navigate = useNavigate();
  const role = localStorage.getItem("userRole");

  useEffect(() => {
    const comboBox = document.querySelector('ui5-comboBox');
    const logAllEvents = (e) => console.log(e.type, e);
    comboBox?.addEventListener('selection-change', logAllEvents);
    comboBox?.addEventListener('change', logAllEvents);
    return () => {
      comboBox?.removeEventListener('selection-change', logAllEvents);
      comboBox?.removeEventListener('change', logAllEvents);
    };
  }, []);

  // Create a ref for the toast component
  const toastRef = useRef(null);

  const showToast = (message) => {
    if (toastRef.current) {
      toastRef.current.textContent = message;
      toastRef.current.open = true;
    }
  };

  const fetchHandledAlerts = async () => {
    try {
      setIsLoading(true);
      const response = await fetch("https://icttestalarm.com:3000/api/view-records");
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API response error:", response.status, errorText);
        setAlerts([]);
        setFilteredAlerts([]);
        showToast("Error fetching data. Please try again.");
        return;
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setAlerts(data);
        setFilteredAlerts(data);
        // showToast("Data refreshed successfully!");
      } else {
        console.error("Unexpected API response format:", data);
        setAlerts([]);
        setFilteredAlerts([]);
        showToast("Unexpected data format received.");
      }
    } catch (error) {
      console.error("Error fetching alerts:", error);
      setAlerts([]);
      setFilteredAlerts([]);
      showToast("Network error. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHandledAlerts();
  }, []);

  useEffect(() => {
    const filtered = alerts.filter((alert) => {
      const alertId = (alert.alert_id || "").toString().toLowerCase();
      const accountNumber = (alert.account_number || "").toString().toLowerCase();
      const status = alert.status || ""; // Use initials from the database
      const date = alert.timestamp
        ? new Date(alert.timestamp).toISOString().split("T")[0] // Format date as YYYY-MM-DD
        : "";
      const time = alert.timestamp
        ? new Date(alert.timestamp).toLocaleTimeString().toLowerCase()
        : "";

      return (
        alertId.includes((filters.alertId || "").toLowerCase()) &&
        accountNumber.includes((filters.accountNumber || "").toLowerCase()) &&
        (!filters.status || status === filters.status) &&
        (!filters.date || date === filters.date) && // Match exact date
        time.includes((filters.time || "").toLowerCase()) // Add time filter condition
      );
    });
    setFilteredAlerts(filtered);
  }, [filters, alerts]);

  const handleFilterChange = (e, field) => {
    setFilters({
      ...filters,
      [field]: e.target.value
    });
  };

  const handleStatusChange = (e) => {
    setFilters({
      ...filters,
      status: e.target.value, // Use the selected value from the standard <select>
    });
  };

  const handleDateChange = (e) => {
    setFilters({
      ...filters,
      date: e.target.value, // Update the date filter with the selected value
    });
  };

  const handleClearFilters = () => {
    setFilters({
      alertId: "",
      accountNumber: "",
      status: "",
      date: "",
      time: "", // Clear time filter
    });
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "#f5f7fa" }}>
        <MenuBar />
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>Loading alerts...</div>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div style={{ minHeight: "100vh", background: "#f5f7fa" }}>
        <MenuBar />
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <p>No alert data available</p>
          <ui5-button onClick={fetchHandledAlerts}>Refresh Data</ui5-button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <MenuBar />
      <div style={{
        padding: '2rem',
        maxWidth: '1200px',
        margin: '2rem auto',
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <ui5-toast ref={toastRef}></ui5-toast>
        {/* Header */}
        <ui5-bar design="Header" style={{ width: '100%' }}>
          <h2 slot="startContent" style={{ margin: 0, color: 'white' }}>Handled & Completed Alerts</h2>
        </ui5-bar>
        {/* Filter Bar */}
        <div style={{ width: '100%', marginBottom: "1rem" }}>
          <ui5-bar design="Header" style={{ width: '100%' }}>
            <div slot="startContent" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem',
              width: '100%',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <ui5-label>Filters:</ui5-label>
                <ui5-input 
                  placeholder="Alert ID" 
                  value={filters.alertId}
                  onInput={(e) => handleFilterChange(e, 'alertId')}
                  style={{ width: '150px' }}
                ></ui5-input>
                <ui5-input 
                  placeholder="Account Number" 
                  value={filters.accountNumber}
                  onInput={(e) => handleFilterChange(e, 'accountNumber')}
                  style={{ width: '180px' }}
                ></ui5-input>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <label htmlFor="statusFilter">Status:</label>
                <select
                  id="statusFilter"
                  value={filters.status}
                  onChange={handleStatusChange} // Trigger filter on change
                  style={{ width: '180px', padding: '0.5rem' }}
                >
                  <option value="">Select Status</option>
                  <option value="H">Handled</option>
                  <option value="U">Unhandled</option>
                  <option value="C">Completed</option>
                  <option value="I">Invalid Call</option>
                </select>
                <input
                  type="date"
                  value={filters.date}
                  onChange={handleDateChange} // Trigger filtering on date change
                  style={{
                    width: '180px',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '0.25rem',
                    fontSize: '1rem',
                  }}
                />
                {/* <ui5-input
                  placeholder="Time (e.g., 10:00 AM)"
                  value={filters.time}
                  onInput={(e) => handleFilterChange(e, 'time')} // Add time filter input
                  style={{ width: '180px' }}
                ></ui5-input> */}
                <ui5-button design="Transparent" onClick={handleClearFilters}>Clear</ui5-button>
                <ui5-button design="Emphasized" onClick={fetchHandledAlerts}>Refresh Data</ui5-button>
              </div>
            </div>
          </ui5-bar>
        </div>
        {/* Table */}
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            boxShadow: "0 0 0.125rem 0 rgba(0,0,0,0.1)",
            borderRadius: "0.25rem",
            background: "#fff"
          }}
        >
          <ui5-table no-data-text="No alerts available" style={{ width: "100%" }}>
            <ui5-table-header-row slot="headerRow">
              <ui5-table-header-cell>Alert ID</ui5-table-header-cell>
              <ui5-table-header-cell>Account Number</ui5-table-header-cell>
              <ui5-table-header-cell>Date</ui5-table-header-cell> {/* Separate Date */}
              <ui5-table-header-cell>Time</ui5-table-header-cell> {/* Separate Time */}
              <ui5-table-header-cell>Latitude</ui5-table-header-cell>
              <ui5-table-header-cell>Longitude</ui5-table-header-cell>
              <ui5-table-header-cell>Status</ui5-table-header-cell>
            </ui5-table-header-row>

            {filteredAlerts.map((alert) => (
              <ui5-table-row key={alert.alert_id}>
                <ui5-table-cell>{alert.alert_id}</ui5-table-cell>
                <ui5-table-cell>{alert.account_number}</ui5-table-cell>
                <ui5-table-cell>
                  {alert.timestamp ? new Date(alert.timestamp).toLocaleDateString() : "-"}
                </ui5-table-cell>
                <ui5-table-cell>
                  {alert.timestamp ? new Date(alert.timestamp).toLocaleTimeString() : "-"}
                </ui5-table-cell>
                <ui5-table-cell>{alert.latitude || "-"}</ui5-table-cell>
                <ui5-table-cell>{alert.longitude || "-"}</ui5-table-cell>
                <ui5-table-cell
                  style={{
                    color: alert.status === "C" ? "green" : alert.status === "H" ? "orange" : "red",
                    fontWeight: "bold",
                  }}
                >
                  {statusMapping[alert.status] || "-"} {/* Map initials to full words */}
                </ui5-table-cell>
              </ui5-table-row>
            ))}
          </ui5-table>
        </div>
      </div>
    </div>
  );
};

export default ViewRecords;